/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  DisplayTypes,
  PageCanvasModel,
  PageIcon,
  PageItemCanvasModel,
  PageItemPosition,
  SbPageItemType,
} from "@/types";
import CanvasManager from "./CanvasManager";
import IconManager from "./IconManager";
import { ImageManager } from "./ImageManager";

import DragAndDrop from "../exercises/DragAndDrop";
import OpenEnd from "../exercises/OpenEnd";
import Matching from "../exercises/Matching";
import UnderlineWord from "../exercises/UnderlineWord";
import FindCorrectOne from "../exercises/FindCorrectOne";
import TrueFalse from "../exercises/TrueFalse";
import MarkObject from "../exercises/MarkObject";
import Tick from "../exercises/Tick";
import FabricCanvasExercise from "../exercises/fabricexercise";
import { BlankPage } from "@/database/userPageDatabaseService";
import { getUserPageDrawings } from "@/database/smartBookModelService";

class PageImageManager {
  private canvasManager: CanvasManager;
  private imageManager: ImageManager;
  public currentExercise: FabricCanvasExercise | null = null;

  constructor(canvasManager: CanvasManager, imageManager: ImageManager) {
    this.canvasManager = canvasManager;
    this.imageManager = imageManager;
  }

  async setPageImage(
    userId: string,
    bookId: string,
    currentPage: PageCanvasModel,
    iconManager: IconManager,
    hideIcons?: boolean,
    callback?: () => void
  ): Promise<void> {
    const drawings = await getUserPageDrawings(
      bookId,
      DisplayTypes.Page,
      currentPage.pageId
    );
    if (drawings) {
      this.canvasManager.loadJson(drawings);
    }
    const center = this.canvasManager["canvas"].getCenter();
    this.canvasManager.setBackgroundImage(
      currentPage.pageImage,
      {
        top: 0,
        left: center.left,
        scaleX: currentPage.ratio,
        scaleY: currentPage.ratio,
        originX: "center",
        originY: "top",
        erasable: false,
        pageIcon: true,
        selectable: false,
      },
      () => {
        const backgroundCoordinate =
          this.canvasManager.getBackgroundImageCoordinate();
        if (!hideIcons) {
          this._loadIcons(
            backgroundCoordinate.imgLeft,
            backgroundCoordinate.imgTop,
            currentPage.pageIcons,
            iconManager,
            callback
          );
        }
      }
    );
  }

  async setQuestionImage(
    currentPageItem: PageItemCanvasModel,
    pageItemPosition: PageItemPosition,
    iconManager: IconManager,
    callback?: () => void
  ) {
    const {
      xInCanvas,
      yInCanvas,
      widthInCanvas,
      heightInCanvas,
      baseLeftPosition,
      ratio,
      backgroundImage,
      backgroundImageScale,
      id,
    } = currentPageItem;
    const canvasSize = this.canvasManager.getCanvasSize();

    let imageLeftPosition = this.getQuestionLeftPosition(
      canvasSize.width,
      ((currentPageItem?.rootPosition?.widthInCanvas || 0) + widthInCanvas) *
        ratio +
        baseLeftPosition,
      pageItemPosition,
      baseLeftPosition
    );

    const pageImage = backgroundImage
      ? backgroundImage
      : currentPageItem.pageImage;

    if (currentPageItem.rootPosition && !backgroundImage) {
      this.imageManager.addImage(pageImage, {
        scale: ratio,
        top: 0,
        left: imageLeftPosition,
        scaleX: ratio,
        scaleY: ratio,
        ratioY: ratio,
        ratioX: ratio,
        cropX: currentPageItem.rootPosition.xInCanvas,
        cropY: currentPageItem.rootPosition.yInCanvas,
        width: currentPageItem.rootPosition.widthInCanvas,
        height: currentPageItem.rootPosition.heightInCanvas,
        erasable: false,
        pageIcon: true,
        selectable: false,
      });
      imageLeftPosition =
        (imageLeftPosition || 0) +
        currentPageItem.rootPosition.widthInCanvas * ratio;
    }

    let backgroundImageOptions: any = {
      id: `page-${id}`,
      top: 0,
      left: imageLeftPosition,
      erasable: false,
      originX: "left",
    };
    if (backgroundImage) {
      backgroundImageOptions = {
        ...backgroundImageOptions,
        scaleX: backgroundImageScale,
        scaleY: backgroundImageScale,
        scale: backgroundImageScale,
      };
    } else {
      backgroundImageOptions = {
        ...backgroundImageOptions,
        scaleX: ratio,
        scaleY: ratio,
        cropX: xInCanvas,
        cropY: yInCanvas,
        width: widthInCanvas,
        height: heightInCanvas,
        scale: ratio,
      };
    }
    this.canvasManager.setBackgroundImage(
      pageImage,
      backgroundImageOptions,
      () => {
        const backgroundCoordinate =
          this.canvasManager.getBackgroundImageCoordinate();
        if (currentPageItem.isExercise) {
          this.configureExercise(currentPageItem);
        }
        this._loadIcons(
          backgroundCoordinate.imgLeft,
          backgroundCoordinate.imgTop,
          currentPageItem.pageIcons,
          iconManager,
          () => {
            if (callback) callback();
          }
        );
      }
    );
  }

  async setBlankPage(currentBlankPage: BlankPage): Promise<void> {
    this.canvasManager.clearCanvas();
    const size = this.canvasManager.getCanvasSize();
    if (currentBlankPage.json) {
      this.canvasManager["canvas"].loadFromJSON(currentBlankPage.json);
    }

    if (currentBlankPage.cropArea) {
      const {
        pageImage,
        left,
        top,
        width,
        height: cropHeight,
      } = currentBlankPage.cropArea;
      const page = pageImage;

      const scale = (size.height - 200) / cropHeight;
      this.imageManager.addImage(page, {
        cropX: left,
        cropY: top,
        width: width,
        height: cropHeight,
        scaleX: scale,
        scaleY: scale,
        selectable: false,
      });
      this.canvasManager.canvasRenderAll();
    }
  }

  private getQuestionLeftPosition = (
    canvasWidth: number,
    imageWidth: number,
    position: PageItemPosition,
    baseLeftPosition: number
  ) => {
    if (position === "left") {
      return baseLeftPosition;
    } else if (position === "right") {
      return canvasWidth - imageWidth;
    } else if (position === "center") {
      return (canvasWidth - imageWidth) / 2;
    }
  };

  private _loadIcons(
    startLeftPosition: number,
    startTopPosition: number,
    pageIcons: PageIcon[],
    iconManager: IconManager,
    callback?: () => void
  ): void {
    Promise.all(
      pageIcons.map(
        (icon) =>
          new Promise<void>((resolve) => {
            iconManager.addIcon(
              startLeftPosition,
              startTopPosition,
              icon,
              () => {
                resolve();
              }
            );
          })
      )
    ).then(() => {
      if (callback) callback();
    });
  }

  private configureExercise(currentPageItem: PageItemCanvasModel): void {
    if (this.currentExercise) {
      this.currentExercise.destroy();
      this.currentExercise = null;
    }
    switch (currentPageItem.pageItemType) {
      case SbPageItemType.DragDrop:
        this.currentExercise = new DragAndDrop(this.canvasManager);
        this.currentExercise.configureCanvas({
          currentPageItem,
          onQuestionFileClick: () => {},
          activityBackgroundImage: "",
          tool: "",
        });
        break;
      case SbPageItemType.OpenEnded:
        this.currentExercise = new OpenEnd(this.canvasManager);
        this.currentExercise.configureCanvas({
          currentPageItem,
          onQuestionFileClick: () => {},
          activityBackgroundImage: "",
          tool: "",
        });
        break;
      case SbPageItemType.Matching:
        this.currentExercise = new Matching(this.canvasManager);
        this.currentExercise.configureCanvas({
          currentPageItem,
          onQuestionFileClick: () => {},
          activityBackgroundImage: "",
          tool: "",
        });
        break;
      case SbPageItemType.Underline:
        this.currentExercise = new UnderlineWord(this.canvasManager);
        this.currentExercise.configureCanvas({
          currentPageItem,
          onQuestionFileClick: () => {},
          activityBackgroundImage: "",
          tool: "",
        });
        break;
      case SbPageItemType.FindCorrectOne:
        this.currentExercise = new FindCorrectOne(this.canvasManager);
        this.currentExercise.configureCanvas({
          currentPageItem,
          onQuestionFileClick: () => {},
          activityBackgroundImage: "",
          tool: "",
        });
        break;
      case SbPageItemType.TrueFalse:
        this.currentExercise = new TrueFalse(this.canvasManager);
        this.currentExercise.configureCanvas({
          currentPageItem,
          onQuestionFileClick: () => {},
          activityBackgroundImage: "",
          tool: "",
        });
        break;
      case SbPageItemType.MarkObject:
        this.currentExercise = new MarkObject(this.canvasManager);
        this.currentExercise.configureCanvas({
          currentPageItem,
          onQuestionFileClick: () => {},
          activityBackgroundImage: "",
          tool: "",
        });
        break;
      case SbPageItemType.Tick:
        this.currentExercise = new Tick(this.canvasManager);
        this.currentExercise.configureCanvas({
          currentPageItem,
          onQuestionFileClick: () => {},
          activityBackgroundImage: "",
          tool: "",
        });
        break;
    }
  }
  onMouseDown(event: MouseEvent): void {
    this.currentExercise?.doMouseDown(event);
  }
  onMouseUp(event: MouseEvent): void {
    this.currentExercise?.doMouseUp(event);
  }

  completeActivity(): void {
    this.currentExercise?.onComplete();
  }

  destroyActivities(): void {
    this.currentExercise?.destroy();
  }

  onZoomChange(): void {
    this.currentExercise?.onZoomChange();
  }
}
export default PageImageManager;
